import { spring, onScroll } from '../vendor/anime.esm.js';

/**
 * MOTION TOKENS (Design System)
 * Sistema centralizado de tiempos y físicas para Anime.js v4.
 * Ningún bloque de la web debe definir duraciones harcodeadas.
 */

export const IS_TOUCH = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
export const PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Heurística (conservadora) de dispositivo de gama baja. Sirve para degradar
// efectos pesados (p.ej. fondos canvas) en móviles débiles sin penalizar al resto.
export const IS_LOW_END = (function () {
    try {
        var nav = window.navigator || {};
        if (nav.connection && nav.connection.saveData) return true;                                   // Ahorro de datos
        if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true;               // ≤ 2 GB RAM
        if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 2) return true;  // ≤ 2 núcleos
    } catch (e) { /* navigator incompleto: asumir no gama-baja */ }
    return false;
})();

// Gate unificado de movimiento ligero: true si el usuario pidió menos movimiento
// (accesibilidad) O el dispositivo es de gama baja (rendimiento). Úsalo para decidir
// si activar efectos costosos. NO es lo mismo que PREFERS_REDUCED_MOTION a secas.
export function prefersLightweight() {
    return PREFERS_REDUCED_MOTION || IS_LOW_END;
}

// Duraciones base (ms) — premium: las animaciones deben "respirar"
export const DURATION = {
    fast: 500,
    normal: 900,
    slow: 1400,
    dramatic: 2200  // Hero, banner, cta-bar — entran con peso
};

// Soporte de clipPath (para fallback en variantes avanzadas)
export const SUPPORTS_CLIP = CSS.supports('clip-path', 'inset(0)');

// Curvas de aceleración y muelles físicos
export const EASE = {
    snappy: 'outExpo',       // Rápido al inicio, se frena de golpe (para zoom y laterales)
    refined: 'outQuart',     // Elegante y suave (para fade-ups limpios)
    bouncy: typeof spring !== 'undefined' ? spring(1, 120, 14, 0) : 'easeOutBack(1.5)', // Muelle deluxe elástico (retorno suave en fallback)
    organic: 'inOutSine',    // Fluido bidireccional (para rotaciones/flip)
    linear: 'linear'
};

// --- PRESETS DE MUELLE DELUXE (Inspiración Cinematic & Apple Motion) ---
export const SPRING_PRESETS = {
    // deluxe: Entrada flotante, amortiguada, ultra-fluida
    deluxe: typeof spring !== 'undefined' ? spring(1, 100, 16, 0) : 'cubic-bezier(0.16, 1, 0.3, 1)',
    // magnetic: Snap físico elástico e interactivo de alta respuesta
    magnetic: typeof spring !== 'undefined' ? spring(1, 140, 12, 0) : 'easeOutElastic(1, .5)',
    // cinematic: Transición flotante, lenta, majestuosa y de peso orgánico
    cinematic: typeof spring !== 'undefined' ? spring(1.2, 50, 14, 0) : 'cubic-bezier(0.22, 1, 0.36, 1)'
};

// Muelles específicos para micro-interacciones (hover)
export const resolveSpring = (mass = 1, stiffness = 100, damping = 10, velocity = 0) => {
    return typeof spring !== 'undefined' 
        ? spring(mass, stiffness, damping, velocity) 
        : 'easeOutElastic(1, .5)';
};

export const HOVER_SPRING = {
    card: typeof spring !== 'undefined' ? spring(1, 260, 22, 0) : 'cubic-bezier(0.16, 1, 0.3, 1)',   // Sutil, flotante
    button: typeof spring !== 'undefined' ? spring(1, 380, 15, 0) : 'easeOutElastic(1, .6)' // Snappy y elástico (CTA)
};

// Tiempos entre elementos escalonados (ms)
export const STAGGER = {
    fast: 100,
    normal: 200,
    slow: 350
};

// Blur tokens — unificación de firma visual
export const BLUR = {
    light:  'blur(2px)',   // Secundarios, textos pequeños
    normal: 'blur(3px)',   // Default para la mayoría
    heavy:  'blur(4px)',   // Primarios, hero, imágenes grandes
    none:   'blur(0px)'    // Destino final
};

/**
 * Funciones resolutoras para adaptar la experiencia a móviles
 * Disminuyen duraciones y staggers en pantallas táctiles para no ralentizar el scroll
 */
export const resolveDuration = (baseDuration) => {
    return IS_TOUCH ? baseDuration * 0.7 : baseDuration;
};

export const resolveStagger = (baseStagger) => {
    return IS_TOUCH ? baseStagger * 0.5 : baseStagger;
};

/**
 * Centraliza el trigger de scroll para todas las variantes.
 * 'bottom -= 80px' = la animación arranca cuando el TOP del bloque
 * está a 80px del borde inferior del viewport. Esto da al usuario
 * tiempo de ver toda la animación antes de que el bloque llegue al centro.
 *
 * @param {Element} el - El bloque DOM
 * @param {boolean} sync - Si true, la animación se sincroniza con el scroll (SVG draw, progress bars)
 */


export const scrollTrigger = (el) => {
    // Solo dispara "play" cuando el elemento entra al viewport.
    // La duración la controla DURATION, no la velocidad del scroll.
    return onScroll({ target: el, enter: 'bottom -= 20%' });
};
